// SPDX-License-Identifier: AGPL-3.0-or-later
import type {ScrollerHandle} from '@app/features/ui/components/Scroller';
import {useCallback, useLayoutEffect, useState} from 'react';

type ResizeType = 'container' | 'content';
export function useScrollerViewport(scrollerRef: React.RefObject<ScrollerHandle | null>) {
	const [viewportSize, setViewportSize] = useState({width: 0, height: 0});
	const [scrollTop, setScrollTop] = useState(0);
	const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
		setScrollTop(event.currentTarget.scrollTop);
	}, []);
	const handleResize = useCallback((entry: ResizeObserverEntry, type: ResizeType) => {
		if (type !== 'container') return;
		const {width, height} = entry.contentRect;
		setViewportSize((prev) => {
			if (prev.width === width && prev.height === height) return prev;
			return {width, height};
		});
	}, []);
	useLayoutEffect(() => {
		if (viewportSize.width > 0 && viewportSize.height > 0) return;
		const node = scrollerRef.current?.getScrollerNode();
		if (!node) return;
		const rect = node.getBoundingClientRect();
		if (rect.width === 0 || rect.height === 0) return;
		setViewportSize({width: rect.width, height: rect.height});
	}, [scrollerRef, viewportSize.width, viewportSize.height]);
	const scrollToTop = useCallback(() => {
		scrollerRef.current?.scrollTo({to: 0, animate: false});
		setScrollTop(0);
	}, [scrollerRef]);
	return {
		viewportSize,
		scrollTop,
		setScrollTop,
		handleScroll,
		handleResize,
		scrollToTop,
	};
}
